import { ArrowRight, CheckCircle2, Phone, Calculator, Clock, Shield, FileCheck, Truck, Package, Euro, ChevronDown } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';
import FAQ from '../components/FAQ';

export default function Preise() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  const pricingPackages = [
    {
      name: '1-2 Zimmer',
      description: 'Ideal für Singles und Paare',
      priceFrom: 'ab CHF 1\'800.–',
      volume: '15-25 m³',
      features: [
        'Komplette Zollabwicklung',
        'Transportversicherung',
        'Professionelle Möbelmontage',
        'Be- und Entladung',
        'Bis 300 km Entfernung',
      ],
      popular: false,
    },
    {
      name: '3-4 Zimmer',
      description: 'Perfekt für Familien',
      priceFrom: 'ab CHF 3\'500.–',
      volume: '35-50 m³',
      features: [
        'Komplette Zollabwicklung',
        'Transportversicherung',
        'Professionelle Möbelmontage',
        'Be- und Entladung',
        'Verpackungsmaterial optional',
        'Bis 300 km Entfernung',
      ],
      popular: true,
    },
    {
      name: '5+ Zimmer',
      description: 'Für große Haushalte',
      priceFrom: 'ab CHF 5\'200.–',
      volume: '60+ m³',
      features: [
        'Komplette Zollabwicklung',
        'Transportversicherung',
        'Professionelle Möbelmontage',
        'Be- und Entladung',
        'Verpackungsservice verfügbar',
        'Flexible Distanzen',
      ],
      popular: false,
    },
  ];

  const additionalServices = [
    {
      icon: Package,
      title: 'Verpackungsservice',
      price: 'ab CHF 45.– pro Stunde',
      description: 'Professionelles Verpacken Ihres kompletten Hausrats',
    },
    {
      icon: Clock,
      title: 'Express-Zuschlag',
      price: 'ab CHF 400.–',
      description: 'Umzug innerhalb 24-48 Stunden realisieren',
    },
    {
      icon: Shield,
      title: 'Zusatzversicherung',
      price: 'ab CHF 150.–',
      description: 'Erweiterte Versicherung für Wertgegenstände',
    },
    {
      icon: Calculator,
      title: 'Zwischenlagerung',
      price: 'ab CHF 120.– pro m³/Monat',
      description: 'Sichere Lagerung in unseren Lagerhallen',
    },
  ];

  const priceFactors = [
    {
      icon: Truck,
      title: 'Umzugsvolumen',
      description: 'Die Menge Ihres Hausrats bestimmt die benötigte LKW-Größe und Anzahl der Fahrten.',
    },
    {
      icon: Calculator,
      title: 'Entfernung',
      description: 'Die Distanz von Ihrem Standort in der Schweiz zum Zielort in Deutschland.',
    },
    {
      icon: Clock,
      title: 'Zeitaufwand',
      description: 'Komplexität des Umzugs, Stockwerke, Aufzug vorhanden, Möbelmontage.',
    },
    {
      icon: FileCheck,
      title: 'Zusatzleistungen',
      description: 'Verpackungsservice, Endreinigung, Zwischenlagerung oder Express-Service.',
    },
  ];

  const benefits = [
    'Fixpreis-Garantie ohne versteckte Kosten',
    'Kostenlose Besichtigung vor Ort oder per Video',
    'Transparente Preisgestaltung',
    'Keine Anzahlung erforderlich',
    'Flexible Zahlungsmöglichkeiten',
    'Rechnung nach Abschluss des Umzugs',
  ];

  const popularRoutes = [
    { from: 'Zürich', to: 'München', price: 'ab CHF 2\'200.–', time: '1 Tag' },
    { from: 'Basel', to: 'Freiburg', price: 'ab CHF 1\'900.–', time: '1 Tag' },
    { from: 'Bern', to: 'Stuttgart', price: 'ab CHF 2\'400.–', time: '1 Tag' },
    { from: 'Zürich', to: 'Berlin', price: 'ab CHF 3\'800.–', time: '1-2 Tage' },
    { from: 'Basel', to: 'Frankfurt', price: 'ab CHF 2\'600.–', time: '1 Tag' },
    { from: 'Luzern', to: 'Hamburg', price: 'ab CHF 4\'200.–', time: '1-2 Tage' },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative pt-24 md:pt-32 pb-24 min-h-[700px] bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-slate-500/5 animate-gradient"></div>
          <img
            src="/umzugsunternehmen-schweiz-deutschland.jpeg"
            alt="Umzug Preise Schweiz Deutschland"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-6 border border-green-200 animate-fade-in hover:scale-105 transition-transform duration-300">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Transparente Fixpreise ohne versteckte Kosten</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight animate-fade-in-delay">
              Umzug Schweiz Deutschland Kosten
              <span className="block text-slate-700 text-4xl md:text-5xl mt-2">
                Umzug Schweiz Deutschland Preise & Angebot
              </span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay-2">
              Transparente Umzug Schweiz Deutschland Preise ab CHF 3'900. Umzugsfirma Schweiz Deutschland mit Fixpreis-Garantie. Jetzt kostenlose Offerte für Ihren Umzug Schweiz Deutschland anfordern!
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12 animate-fade-in-delay-2">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
              >
                <span>Jetzt kostenloses Angebot erhalten</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-white hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-800"
              >
                0800 700 100 (Gratis)
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-16">
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">100%</div>
                <div className="text-slate-600 text-sm">Fixpreis-Garantie</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">0 CHF</div>
                <div className="text-slate-600 text-sm">Besichtigung & Offerte</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">24h</div>
                <div className="text-slate-600 text-sm">Offerte innerhalb 24h</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={scrollDown}
            className="animate-bounce bg-white hover:bg-slate-50 text-slate-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-slate-200"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Umzug Schweiz Deutschland Kosten - Preisübersicht
            </h2>

            <div className="prose prose-lg max-w-none text-slate-700 mb-12">
              <p className="text-xl leading-relaxed text-center">
                Transparente <strong>Umzug Schweiz Deutschland Preise</strong>: Wählen Sie das passende Paket für Ihren <strong>Umzug Schweiz Deutschland</strong>. Alle <strong>Umzugskosten Schweiz Deutschland</strong> inklusive <strong>Zollabwicklung</strong>, <strong>Transportversicherung</strong> und professioneller Abwicklung. <strong>Umzug Schweiz Deutschland Angebot</strong> mit Fixpreis-Garantie.
              </p>
              <p className="text-lg leading-relaxed text-center mt-4">
                Ob <Link to="/umzug-zuerich-deutschland" className="text-slate-900 font-semibold hover:underline">Umzug Zürich Deutschland</Link>, <Link to="/umzug-basel-deutschland" className="text-slate-900 font-semibold hover:underline">Umzug Basel Deutschland</Link>, <Link to="/umzug-bern-deutschland" className="text-slate-900 font-semibold hover:underline">Umzug Bern Deutschland</Link> oder <Link to="/umzug-luzern-deutschland" className="text-slate-900 font-semibold hover:underline">Umzug Luzern Deutschland</Link> – wir bieten faire Preise mit <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Komplettumzug</Link> und <Link to="/zollabwicklung" className="text-slate-900 font-semibold hover:underline">Zollabwicklung</Link>.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
            {pricingPackages.map((pkg, index) => (
              <div
                key={index}
                className={`relative bg-slate-50 rounded-2xl p-8 border-2 ${
                  pkg.popular ? 'border-slate-800 shadow-2xl scale-105' : 'border-slate-100'
                } hover:shadow-xl hover:-translate-y-2 transition-all duration-300`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-slate-800 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Beliebteste Option
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-slate-600 mb-4">{pkg.description}</p>
                  <div className="text-4xl font-bold text-slate-800 mb-2">
                    {pkg.priceFrom}
                  </div>
                  <p className="text-sm text-slate-500">Umzugsvolumen: {pkg.volume}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToContact}
                  className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-slate-800 text-white hover:bg-slate-900 shadow-lg hover:shadow-xl'
                      : 'bg-white text-slate-800 border-2 border-slate-800 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  Jetzt Angebot anfordern
                </button>
              </div>
            ))}
          </div>

          <div className="text-center text-slate-600 max-w-3xl mx-auto">
            <p className="text-sm">
              * Die angegebenen Preise sind Richtwerte für Standardumzüge bis 300 km Entfernung. Der endgültige Preis wird nach einer kostenlosen Besichtigung festgelegt und gilt als Fixpreis ohne versteckte Kosten.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Beliebte Routen mit Preisbeispielen
            </h2>
            <p className="text-xl text-slate-600 text-center">
              Orientierungspreise für 3-Zimmer-Wohnungen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {popularRoutes.map((route, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="font-bold text-slate-900 text-lg">{route.from}</div>
                    <ArrowRight className="w-5 h-5 text-slate-400 my-2" />
                    <div className="font-bold text-slate-900 text-lg">{route.to}</div>
                  </div>
                  <div className="bg-slate-800 text-white px-3 py-1 rounded-lg text-sm font-semibold">
                    {route.time}
                  </div>
                </div>
                <div className="text-2xl font-bold text-slate-800">
                  {route.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Was beeinflusst die Umzugskosten?
            </h2>
            <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto">
              Diese Faktoren bestimmen den Preis Ihres Umzugs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {priceFactors.map((factor, index) => {
              const Icon = factor.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
                >
                  <div className="bg-slate-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {factor.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {factor.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Zusätzliche Dienstleistungen
            </h2>
            <p className="text-xl text-slate-600 text-center">
              Ergänzen Sie Ihr Umzugspaket nach Ihren Wünschen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <div className="text-lg font-bold text-slate-800 mb-2">
                    {service.price}
                  </div>
                  <p className="text-sm text-slate-600">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-12 text-center">
              Unsere Preisgarantie
            </h2>

            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 mb-12">
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-10 text-center text-white">
              <Euro className="w-16 h-16 mx-auto mb-6 text-green-400" />
              <h3 className="text-3xl font-bold mb-4">
                Fixpreis-Garantie
              </h3>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Nach der kostenlosen Besichtigung erhalten Sie von uns einen verbindlichen Festpreis. Dieser Preis gilt garantiert – ohne versteckte Kosten oder nachträgliche Aufschläge.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center space-x-2"
              >
                <span>Jetzt kostenlose Besichtigung vereinbaren</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Bereit für Ihr individuelles Angebot?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Erhalten Sie innerhalb von 24 Stunden Ihre persönliche Offerte mit Fixpreis-Garantie
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2"
              >
                <span>Kostenloses Angebot anfordern</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="flex items-center space-x-2 text-slate-800 hover:text-slate-900 font-semibold transition"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100 (Gratis)</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
}
